import { verboseDebug } from 'classes/helpers/debug.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import {
  INITIAL_LIBRARY_SEARCH_LIMIT,
  MAX_SEARCH_LIMIT,
  PitchListStore,
} from 'components/sections/pitch-list/store/definitions';
import { IVerboseComponent } from 'interfaces/i-components';
import { QueueType } from 'interfaces/i-queue-mode';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { ERROR_MSGS } from 'lib_ts/enums/errors.enums';
import { PLSortMode, PLStoreMode } from 'lib_ts/enums/pitch-list.enums';
import {
  BuildPriority,
  MIN_FREQUENCY,
  PitchType,
} from 'lib_ts/enums/pitches.enums';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import { ISortSelection } from 'lib_ts/interfaces/common/i-option';
import { IPitch } from 'lib_ts/interfaces/pitches';
import { IFilterPitches } from 'lib_ts/interfaces/pitches/i-filter-pitches';
import { ISearchPitches } from 'lib_ts/interfaces/pitches/i-search-pitches';
import { clone, isNil, merge } from 'lodash';
import { PitchesService } from 'services/pitches.service';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';
import { create } from 'zustand';

const sortPitches = (pitches: IPitch[], sortMode: Nullable<ISortSelection>) => {
  const output = clone(pitches);

  if (isNil(sortMode)) {
    return output;
  }

  const sortFn = (a: IPitch, b: IPitch) => {
    switch (sortMode.value as PLSortMode) {
      case PLSortMode.CustomOrder: {
        if (!isNil(a._sort) && !isNil(b._sort)) {
          if (a._sort === b._sort) {
            return 0;
          }

          return a._sort > b._sort ? 1 : -1;
        }

        return StringHelper.safeSort(a._created, b._created);
      }

      case PLSortMode.Alphabetical: {
        return StringHelper.safeSort(a.name, b.name);
      }

      case PLSortMode.Frequency: {
        const [aValue, bValue] = [a, b].map(
          (m) => m.frequency ?? MIN_FREQUENCY
        );

        if (aValue === bValue) {
          return 0;
        }

        return aValue > bValue ? 1 : -1;
      }

      case PLSortMode.PitchType: {
        return StringHelper.safeSort(
          a.type ?? PitchType.None,
          b.type ?? PitchType.None
        );
      }

      case PLSortMode.Speed: {
        const [aValue, bValue] = [a, b].map((m) => m.bs.vnet);

        if (aValue === bValue) {
          return 0;
        }

        return aValue > bValue ? 1 : -1;
      }

      case PLSortMode.NetSpin: {
        const [aValue, bValue] = [a, b].map((m) => m.bs.wnet);

        if (aValue === bValue) {
          return 0;
        }

        return aValue > bValue ? 1 : -1;
      }

      case PLSortMode.BreaksH: {
        if (
          a.priority !== BuildPriority.Breaks &&
          b.priority !== BuildPriority.Breaks
        ) {
          // avoid weird sorting behaviour when only non-break pitches are found
          return 0;
        }

        const [aValue, bValue] = [a, b].map((m) => {
          if (m.priority !== BuildPriority.Breaks || !m.breaks) {
            return -Infinity;
          }

          return m.breaks.xInches;
        });

        if (aValue === bValue) {
          return 0;
        }

        return aValue > bValue ? 1 : -1;
      }

      case PLSortMode.BreaksV: {
        if (
          a.priority !== BuildPriority.Breaks &&
          b.priority !== BuildPriority.Breaks
        ) {
          // avoid weird sorting behaviour when only non-break pitches are found
          return 0;
        }

        const [aValue, bValue] = [a, b].map((m) => {
          if (m.priority !== BuildPriority.Breaks || !m.breaks) {
            return -Infinity;
          }

          return m.breaks.zInches;
        });

        if (aValue === bValue) {
          return 0;
        }

        return aValue > bValue ? 1 : -1;
      }

      default: {
        return 0;
      }
    }
  };

  output.sort((a, b) => sortFn(a, b) * sortMode.dir);

  return output;
};

const DEFAULT_FILTERS: IFilterPitches = {
  name: null,
  year: null,
  trained: null,
  hand: null,
  type: null,
};

const usePitchListStore = create<PitchListStore>((set, get) => {
  // hack to get around passing props
  const vbProps: IVerboseComponent = {
    identifier: 'PitchListStore',
    verbose: false,
  };

  const defaultSearch: ISearchPitches = {
    parent: {},
    list: {
      name: null,
      folder: null,
      parent: null,
    },
    filters: null,
    metricsFilters: null,
  };

  return {
    // BaseSlice
    showRecap: false,
    loading: false,
    setLoading: (loading) => set({ loading }),

    mode: PLStoreMode.Library,
    setMode: (v) => set({ mode: v }),

    // ListState
    pitches: [],

    selectedPitch: undefined,
    pitchListRequestId: undefined,

    // ListActions
    setSelectedPitch: (v) => set({ selectedPitch: v }),

    setPitches: (v) => set({ pitches: v.map((p) => clone(p)) }),

    updatePitches: async (config) => {
      try {
        if (config.payloads.find((p) => !p._id)) {
          NotifyHelper.error({
            message_md: 'Cannot update a pitch with an empty ID.',
          });
          return undefined;
        }

        const { pitches } = get();

        const result = await PitchesService.getInstance().updateMany(
          config.payloads
        );

        if (!result.success) {
          throw new Error(result.error);
        }

        const updatedPitches = result.data as IPitch[];

        const nextPitches = clone(pitches);

        // update the pitches in the original array
        for (const updatedPitch of updatedPitches) {
          const index = nextPitches.findIndex(
            (p) => p._id === updatedPitch._id
          );

          if (index === -1) {
            continue;
          }

          // copy over any TMS value before replacing
          updatedPitch._mx_tms = nextPitches[index]._mx_tms;

          // replace the pitch in the array
          nextPitches.splice(index, 1, updatedPitch);
        }

        set({
          pitches: nextPitches,
        });

        if (config.successMsg) {
          NotifyHelper.success({ message_md: config.successMsg });
        }

        return updatedPitches;
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md:
            e instanceof Error
              ? e.message
              : `There was an error updating your ${
                  config.payloads.length === 1 ? 'pitch' : 'pitches'
                }.`,
        });

        return undefined;
      }
    },

    deletePitches: async (ids, silently) => {
      const { pitches } = get();

      try {
        if (!silently) {
          set({ loading: true });
        }

        const result = await PitchesService.getInstance().markAsDeleted(ids);

        if (!result.success) {
          throw new Error(result.error);
        }

        if (!silently) {
          NotifyHelper.success({
            message_md: `${ids.length === 1 ? 'Pitch' : 'Pitches'} deleted!`,
          });
        }

        const nextPitches = pitches.filter((p) => !ids.includes(p._id));

        set({
          pitches: nextPitches,
          loading: false,
        });

        return true;
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md:
            e instanceof Error
              ? e.message
              : `There was an error deleting your pitch. ${ERROR_MSGS.CONTACT_SUPPORT}`,
        });

        if (!silently) {
          set({ loading: false });
        }

        return false;
      }
    },

    reloadPitches: async (config) => {
      try {
        const { filters, metricsFilters, searchCriteria, sortMode } = get();

        if (!config.activeList) {
          // LIBRARY CASE
          set({ loading: true });

          const foundPitches = await PitchesService.getInstance().search(
            {
              ...searchCriteria,
              filters: filters,
              metricsFilters: metricsFilters,
            },
            MAX_SEARCH_LIMIT
          );

          verboseDebug(
            vbProps,
            `reloadPitches > ${foundPitches.length} pitches`
          );

          foundPitches.forEach((p) => {
            p._mx_tms = useTrainedMsStore.getState().getTms(p.bs_hash);
          });

          verboseDebug(vbProps, `reloadPitches > TMS mixin finished`);

          const nextPitches = sortPitches(foundPitches, sortMode);

          set({
            pitches: nextPitches,
            loading: false,
          });
          return;
        }

        // PITCH LIST CASE
        set({ loading: true });

        const foundPitches = await PitchesService.getInstance().getList(
          config.activeList._id,
          searchCriteria
        );

        verboseDebug(vbProps, `loaded list > ${foundPitches.length} pitches`);

        foundPitches.forEach((p) => {
          p._mx_tms = useTrainedMsStore.getState().getTms(p.bs_hash);
        });

        verboseDebug(vbProps, `loaded list > TMS mixin finished`);

        const nextPitches = sortPitches(foundPitches, sortMode);

        set({
          pitches: nextPitches,
          loading: false,
        });
      } catch (e) {
        console.error(e);
        set({ loading: false });
      }
    },

    mixinTms: () => {
      const { pitches } = get();

      if (pitches.length === 0) {
        // no pitches loaded
        return;
      }

      let totalChanges = 0;

      const nextPitches = pitches.map((p) => {
        const tms = useTrainedMsStore.getState().getTms(p.bs_hash);

        if (p._mx_tms === tms) {
          return p;
        }

        totalChanges++;

        const nextPitch = clone(p);

        if (!tms) {
          delete nextPitch._mx_tms;
          return nextPitch;
        }

        nextPitch._mx_tms = tms;
        return nextPitch;
      });

      if (totalChanges === 0) {
        // no changes => don't change pitches to avoid unnecessary re-render
        return;
      }

      verboseDebug(vbProps, `mixinTms > ${totalChanges} pitches changed`);
      set({ pitches: nextPitches });
    },

    // SearchState
    sortMode: {
      value: PLSortMode.CustomOrder,
      dir: 1,
    },

    search: false,

    defaultSearchCriteria: defaultSearch,

    searchCriteria: defaultSearch,

    // SearchActions
    setSortMode: (nextSortMode) => {
      const { pitches } = get();

      const nextPitches = sortPitches(pitches, nextSortMode);

      set({
        pitches: nextPitches,
        sortMode: nextSortMode,
      });
    },

    resetSearchCriteria: async (config) => {
      const { mode, defaultSearchCriteria, sortMode } = get();

      const nextMode = config.mode ?? mode;

      const nextCriteria = clone(config.criteria ?? defaultSearchCriteria);
      nextCriteria.filters = clone(DEFAULT_FILTERS);
      nextCriteria.metricsFilters = null;

      if (nextMode === PLStoreMode.Library) {
        delete nextCriteria.parent._parent_id;
      }

      const requestId = Date.now();

      set({
        pitchListRequestId: requestId,
        mode: nextMode,
        defaultSearchCriteria: nextCriteria,
        searchCriteria: nextCriteria,
        filters: nextCriteria.filters,
        metricsFilters: nextCriteria.metricsFilters,
        pitches: [],
        filteredPitches: [],
        loading: true,
      });

      // if we're loading the Pitch Library, grab only the first 500 first for quicker first paint
      const limit =
        nextMode === PLStoreMode.Library
          ? INITIAL_LIBRARY_SEARCH_LIMIT
          : MAX_SEARCH_LIMIT;

      const firstBatchPitches = await PitchesService.getInstance().search(
        nextCriteria,
        limit
      );

      verboseDebug(
        vbProps,
        `resetSearchCriteria > ${firstBatchPitches.length} pitches`
      );

      firstBatchPitches.forEach((p) => {
        p._mx_tms = useTrainedMsStore.getState().getTms(p.bs_hash);
      });

      verboseDebug(
        vbProps,
        `resetSearchCriteria > TMS mixin first batch finished`
      );

      const nextPitches = sortPitches(firstBatchPitches, sortMode);

      // to prevent race condition edge case where the user has switched lists but an earlier request returns later
      // e.g. user attempts to load Library, then quickly switches to Personal List before the Library request returns
      if (get().pitchListRequestId !== requestId) {
        return;
      }

      set({
        pitches: nextPitches,
        loading: false,
      });

      if (nextMode === PLStoreMode.PitchList) {
        return;
      }

      // load the reset of the library and silently update
      const secondBatchPitches = await PitchesService.getInstance().search(
        {
          ...nextCriteria,
          skip: firstBatchPitches.length,
        },
        MAX_SEARCH_LIMIT
      );

      // user navigated away between first and second load
      if (get().pitchListRequestId !== requestId) {
        return;
      }

      secondBatchPitches.forEach((p) => {
        p._mx_tms = useTrainedMsStore.getState().getTms(p.bs_hash);
      });

      verboseDebug(
        vbProps,
        `resetSearchCriteria > TMS mixin second batch finished`
      );

      const allPitches = [get().pitches, secondBatchPitches].flat();
      const finalPitches = sortPitches(allPitches, get().sortMode);

      set({
        pitches: finalPitches,
        loading: false,
      });
    },

    mergeSearchCriteria: async (config) => {
      const { filters, metricsFilters, searchCriteria, sortMode } = get();

      const nextCriteria: ISearchPitches = {
        ...searchCriteria,
        ...config.criteria,
        filters: filters,
        metricsFilters: metricsFilters,
      };

      set({
        searchCriteria: nextCriteria,
        // avoid flickering old results before new results can render
        pitches: [],
        filteredPitches: [],
        loading: true,
      });

      const foundPitches = await PitchesService.getInstance().search(
        nextCriteria,
        MAX_SEARCH_LIMIT
      );

      verboseDebug(
        vbProps,
        `mergeSearchCriteria > ${foundPitches.length} pitches`
      );

      foundPitches.forEach((p) => {
        p._mx_tms = useTrainedMsStore.getState().getTms(p.bs_hash);
      });

      verboseDebug(vbProps, `mergeSearchCriteria > TMS mixin finished`);

      const nextPitches = sortPitches(foundPitches, sortMode);

      set({
        pitches: nextPitches,
        loading: false,
      });
    },

    // FilterState
    filters: clone(DEFAULT_FILTERS),
    metricsFilters: null,
    filteredPitches: [],

    // FilterActions
    mergeFilters: (v) => {
      const { filters } = get();

      const nextFilter = clone(filters);
      merge(nextFilter, v);

      set({ filters: nextFilter });
    },

    setMetricsFilters: (v) => set({ metricsFilters: v }),

    setFilteredPitches: (v) => set({ filteredPitches: v }),

    resetFilters: () => {
      set({ filters: clone(DEFAULT_FILTERS), metricsFilters: null });
    },

    // QueueState
    queueType: QueueType.RepeatOne,
    queuePitchIDs: [],

    // QueueActions
    setQueueType: (v) => set({ queueType: v }),
    setQueuePitchIDs: (v) => set({ queuePitchIDs: v }),

    // DialogState
    dialog: undefined,

    // DialogActions
    openDialog: (args) => {
      set({
        dialog: {
          key: Date.now(),
          mode: args.dialog,
          pitches: args.pitches,
          trainingPitches: args.trainingPitches,
        },
      });
    },

    setDialogPitches: (pitches) => {
      const { dialog } = get();

      set({
        dialog: {
          ...dialog,
          pitches: pitches,
        },
      });
    },

    closeDialog: () => {
      const { dialog: current } = get();

      set({
        dialog: {
          key: undefined,
          mode: undefined,
          // keep pitches intact (e.g. for follow-up after training ends)
          pitches: current?.pitches,
          trainingPitches: current?.trainingPitches,
        },
      });
    },
  };
});

export default usePitchListStore;
